/**
 * Framework-neutral chat access to one platform service: the TypeScript
 * mirror of the Python SDK's `socaity.integrations.ChatServiceAdapter`.
 *
 * Hides the platform protocol behind three calls: `complete` (awaited),
 * `submit` (job handle) and `streamChunks` (typed OpenAI-style chunk
 * objects). Framework adapters (LangChain.js, ...) compose it and only
 * translate between the framework's message types and the OpenAI-compatible
 * wire format of `sdk/schemas/ai.ts`.
 *
 * Every call runs as a platform job. The adapter keeps the handles of the
 * jobs it submitted so frameworks can inspect progress and job ids without
 * touching client internals.
 */

import { InferenceBackendClient } from '../clients/gateway/client';
import { loadServiceDefinition } from '../clients/gateway/openapi';
import { Job } from '../runtime/job';
import { EndpointDefinition } from '../schemas/definitions';
import { ChatCompletionChunk, ChatCompletionRequest, ChatCompletionResponse } from '../schemas/ai';

export interface ChatServiceAdapterOptions {
  /** Per-adapter API key; falls back to the global configuration. */
  apiKey?: string;
  /** Chat endpoint path override. Default: an endpoint with a `messages` parameter, else `/chat`. */
  endpointPath?: string;
}

/** Progress/status snapshot of one submitted job. */
export interface JobStatusSnapshot {
  job_id?: string;
  status?: string;
  is_terminal: boolean;
  progress: number | null;
  error?: string;
}

export class ChatServiceAdapter {
  readonly client: InferenceBackendClient;
  readonly endpoint: EndpointDefinition;
  readonly jobs: Job[] = [];

  private constructor(client: InferenceBackendClient, endpoint: EndpointDefinition) {
    this.client = client;
    this.endpoint = endpoint;
  }

  /**
   * Connect to a chat-capable service.
   *
   * @param service Service URL or an already connected client (reused as-is).
   */
  static async connect(
    service: string | InferenceBackendClient,
    options: ChatServiceAdapterOptions = {},
  ): Promise<ChatServiceAdapter> {
    const client = typeof service === 'string'
      ? new InferenceBackendClient(await loadServiceDefinition(service), {
          address: service,
          apiKey: options.apiKey,
        })
      : service;
    return new ChatServiceAdapter(client, resolveChatEndpoint(client, options.endpointPath));
  }

  // ------------------------------------------------------------------
  // Calls
  // ------------------------------------------------------------------

  /** Submit one chat request as a platform job and record the handle. */
  submit(request: ChatCompletionRequest): Job {
    const job = this.client.submitJob(this.endpoint.path, request as Record<string, any>);
    this.jobs.push(job);
    return job;
  }

  /** Awaited chat completion; returns a ChatCompletionResponse-shaped object. */
  async complete(request: ChatCompletionRequest): Promise<ChatCompletionResponse> {
    const result = await this.submit({ ...request, stream: false }).getResult();
    return asResponse(result);
  }

  /** Stream ChatCompletionChunk-shaped objects. */
  async *streamChunks(request: ChatCompletionRequest): AsyncGenerator<ChatCompletionChunk> {
    const session = await this.submit({ ...request, stream: true }).stream();
    try {
      for await (const chunk of session.iterChunks()) {
        yield asChunk(chunk);
      }
    } finally {
      await session.close();
    }
  }

  // ------------------------------------------------------------------
  // Job introspection
  // ------------------------------------------------------------------

  static jobStatus(job: Job): JobStatusSnapshot {
    return {
      job_id: job.id,
      status: job.status,
      is_terminal: job.isTerminal,
      progress: job.progress,
      error: job.response?.error ?? undefined,
    };
  }

  lastJob(): Job | undefined {
    return this.jobs[this.jobs.length - 1];
  }
}

// ---------------------------------------------------------------------------
// Endpoint resolution & result normalization
// ---------------------------------------------------------------------------

function resolveChatEndpoint(client: InferenceBackendClient, endpointPath?: string): EndpointDefinition {
  const endpoints = client.definition.endpoints;
  if (endpointPath) {
    const endpoint = endpoints.find((e) => e.path === endpointPath);
    if (!endpoint) throw new Error(`Service has no endpoint '${endpointPath}'.`);
    return endpoint;
  }
  // The chat contract is recognizable by its `messages` parameter; prefer the
  // conventional /chat path when several endpoints qualify.
  const chatEndpoints = endpoints.filter(
    (e) => e.parameters.some((p) => p.name === 'messages'),
  );
  const byPath = chatEndpoints.find((e) => e.path.replace(/\/+$/, '').endsWith('/chat'));
  const endpoint = byPath ?? chatEndpoints[0];
  if (!endpoint) throw new Error('Service exposes no chat endpoint (no `messages` parameter found).');
  return endpoint;
}

/** Normalize a job result into a ChatCompletionResponse-shaped object. */
function asResponse(result: any): ChatCompletionResponse {
  if (result && typeof result === 'object' && Array.isArray(result.choices)) return result;
  if (typeof result === 'string') {
    return {
      object: 'chat.completion',
      created: Math.floor(Date.now() / 1000),
      choices: [{ index: 0, message: { role: 'assistant', content: result }, finish_reason: 'stop' }],
    };
  }
  throw new Error(`Unexpected chat result shape: ${typeof result}`);
}

/** Normalize one SSE item into a ChatCompletionChunk-shaped object. */
function asChunk(chunk: any): ChatCompletionChunk {
  if (chunk && typeof chunk === 'object') return chunk;
  // Tolerate plain-text SSE data lines from non-standard services.
  return {
    id: '',
    object: 'chat.completion.chunk',
    created: Math.floor(Date.now() / 1000),
    choices: [{ index: 0, delta: { content: String(chunk) }, finish_reason: null }],
  };
}
